import type { Node } from "@xyflow/react";
import { resolveCreatedSize } from "@/lib/frame-defaults";

const FALLBACK_WIDTH = 400;
const FALLBACK_HEIGHT = 300;

type FrameGeometryNode = Pick<Node, "width" | "height" | "style" | "measured"> &
  Partial<Pick<Node, "type" | "data">>;

export function numericFrameDimension(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value !== "string") return undefined;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function frameTypeForNode(
  node: Partial<Pick<Node, "type" | "data">>,
): string | undefined {
  const data = node.data as { frameType?: unknown } | undefined;
  if (typeof data?.frameType === "string") return data.frameType;
  return typeof node.type === "string" ? node.type : undefined;
}

export function frameNodeSize(
  node: FrameGeometryNode,
  fallback: { width?: number; height?: number } = {},
) {
  const frameType = frameTypeForNode(node);
  const defaultSize = frameType
    ? resolveCreatedSize(frameType, { content: node.data?.content })
    : { w: FALLBACK_WIDTH, h: FALLBACK_HEIGHT };
  const fallbackWidth = fallback.width ?? defaultSize.w;
  const fallbackHeight = fallback.height ?? defaultSize.h;
  return {
    width:
      numericFrameDimension(node.width) ??
      numericFrameDimension(node.style?.width) ??
      numericFrameDimension(node.measured?.width) ??
      fallbackWidth,
    height:
      numericFrameDimension(node.height) ??
      numericFrameDimension(node.style?.height) ??
      numericFrameDimension(node.measured?.height) ??
      fallbackHeight,
  };
}

export function frameNodeDimension(
  node: FrameGeometryNode,
  axis: "width" | "height",
): number {
  return frameNodeSize(node)[axis];
}
